// The data rows of the visible page, each followed while expanded by its detail row, or by its child rows
// when the table declares childRows (ChildRows.js). A selected row paints the selected surface; expansion
// alone keeps the resting surface.

import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'

import { RowExpand } from '@CommonComponents/buttons'
import { useTable } from '../context'
import { displayText } from '../format'
import { cellClass } from '../cells/registry'
import { tierOf } from '../geometry/controlSize'
import { ChildRows } from './ChildRows'
import { DetailRow } from './DetailRow'
import { useExpansion } from './useExpansion'
import { useDetailPresence } from './motion'

/**
 * Whether the row is in the current selection. A selection module that publishes its own predicate on the
 * context wins; otherwise the predicate reads the selection state.
 * @param {import('../types').TableContextValue & {selection?: {isSelected?: Function}}} table
 * @param {object} row
 * @param {string} id
 * @returns {boolean}
 */
export const rowSelected = (table, row, id) => {
  if (table.selection && typeof table.selection.isSelected === 'function')
    return Boolean(table.selection.isSelected(id, row))
  const { selection } = table.state
  if (!table.layout.selectable || !selection) return false
  if (selection.scope === 'rows' || selection.scope === 'page')
    return Boolean(selection.ids && selection.ids.has(id))
  if (selection.scope === 'all') {
    const eligible = table.config.selection && table.config.selection.isEligible
    return (
      !(selection.excluded && selection.excluded.has(id)) &&
      (typeof eligible !== 'function' || eligible(row) !== false)
    )
  }
  return false
}

const rowClass = (expandable, open, selected) =>
  [
    'me-table-row',
    expandable ? 'is-expandable' : '',
    expandable && open ? 'is-expanded' : '',
    selected ? 'is-selected' : '',
  ]
    .filter(Boolean)
    .join(' ')

const NO_EDGES = { start: false, end: false }

/** One data cell; a locked column's cell carries its lock placement and, on the innermost lock, its edge. */
export const BodyCell = ({ column, row }) => {
  const { slots, geometry } = useTable()
  const { Cell } = slots
  const lockStyle =
    geometry && typeof geometry.lockStyle === 'function'
      ? geometry.lockStyle(column.key)
      : undefined
  const edges =
    lockStyle && typeof geometry.lockEdges === 'function'
      ? geometry.lockEdges(column.key)
      : NO_EDGES
  const className = [
    cellClass(column),
    lockStyle && 'is-locked',
    edges.start && 'is-lock-start-edge',
    edges.end && 'is-lock-end-edge',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <td
      className={className}
      style={lockStyle}
      data-lock={lockStyle ? column.lock || undefined : undefined}
      data-column-key={column.key}
    >
      <Cell column={column} row={row} />
    </td>
  )
}

BodyCell.propTypes = {
  column: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
}

export const BodyRow = ({ row, expansion }) => {
  const table = useTable()
  const { config, layout, slots, detailPrefix, rowActions, childRows } = table
  const { RowActions, SelectCell } = slots
  const id = String(row[config.rowKey])
  const open = layout.expandable && expansion.isOpen(id)
  const presence = useDetailPresence(open)
  const detailId = `${detailPrefix}-${id}`
  const actions = layout.hasActions ? rowActions(row) : []
  const identity = displayText(layout.idColumn, row)
  const controlRef = useRef(null)

  const returnFocus = useCallback(() => {
    const chevron =
      controlRef.current &&
      controlRef.current.querySelector('[data-row-expand]')
    if (chevron) chevron.focus()
  }, [])

  return (
    <>
      <tr
        className={rowClass(
          layout.expandable,
          open,
          rowSelected(table, row, id)
        )}
        onPointerDown={layout.expandable ? expansion.onPointerDown : undefined}
        onClick={
          layout.expandable ? event => expansion.onClick(event, id) : undefined
        }
      >
        {layout.selectable && <SelectCell row={row} />}
        {layout.expandable && (
          <td className="me-table-control" ref={controlRef}>
            <RowExpand
              label={identity}
              controls={detailId}
              expanded={open}
              compact={tierOf('expand', table.state?.view?.density) === 'xs'}
              onChange={() => expansion.toggle(id)}
            />
          </td>
        )}
        {layout.columns.map(column => (
          <BodyCell key={column.key} column={column} row={row} />
        ))}
        {layout.hasActions && (
          <td className="me-table-actions">
            <RowActions actions={actions} identity={identity} />
          </td>
        )}
      </tr>
      {layout.expandable && childRows && open && (
        <ChildRows id={detailId} row={row} />
      )}
      {layout.expandable && !childRows && presence.present && (
        <DetailRow
          id={detailId}
          row={row}
          leaving={presence.leaving}
          onExited={presence.onExited}
          returnFocus={returnFocus}
        />
      )}
    </>
  )
}

BodyRow.propTypes = {
  row: PropTypes.object.isRequired,
  expansion: PropTypes.shape({
    isOpen: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    onPointerDown: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
  }).isRequired,
}

export const BodyRows = () => {
  const { config, derived } = useTable()
  const expansion = useExpansion()
  return derived.pageRows.map(row => (
    <BodyRow key={String(row[config.rowKey])} row={row} expansion={expansion} />
  ))
}
